import { Box } from "@mui/material";
import { BarElement, CategoryScale, Chart as ChartJS, Legend as LegendPlugin, LinearScale, LogarithmicScale, Title, Tooltip as TooltipPlugin } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useMemo, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import DownloadComponentButton from "../button/DownloadComponentButton";
import { formatTo2Decimals } from "../grid/columnDefinitions/columnDefinitions";

ChartJS.register(CategoryScale, LinearScale, LogarithmicScale, BarElement, Title, TooltipPlugin, LegendPlugin, ChartDataLabels);
const TrackViewBarChart = ({ data, filename, totalValue, averageValue, totalLabel, averageLabel, title, unit }) => {
  const [visibility, setVisibility] = useState(false);
  const ref = useRef(null);
  const sortedData = useMemo(() => {
    if (!data?.labels?.length || !data.datasets?.length) return data;
    const ds = data.datasets[0];

    const rows = data.labels.map((label, i) => {
      const raw = ds.data[i];
      const value = typeof raw === "object" && raw !== null ? raw.x ?? raw.y ?? raw.value ?? 0 : Number(raw ?? 0);
      return { label, value, raw }; // keep raw to preserve object shape if needed
    });

    rows.sort((a, b) => b.value - a.value); // DESC. Use a.value - b.value for ASC

    return {
      ...data,
      labels: rows.map((r) => r.label),
      datasets: [
        {
          ...ds,
          data: rows.map((r) => r.raw), // keep original shape (number or {x,y})
        },
      ],
    };
  }, [data]);
  const logSafeData = useMemo(() => {
    if (!sortedData?.datasets?.length) return sortedData;
    const ds0 = sortedData.datasets[0];

    const safe = ds0.data.map((raw) => {
      const v = raw == null ? null : typeof raw === "object" ? raw.x ?? raw.y ?? raw.value ?? null : raw;

      // If value is null/undefined/<=0, return null to skip plotting on log scale
      if (v == null || Number(v) <= 0) return null;

      // preserve original shape (number or object) when valid
      return raw;
    });

    return {
      ...sortedData,
      datasets: [{ ...ds0, data: safe }],
    };
  }, [sortedData]);
  const options = {
    indexAxis: "y",
    scales: {
      x: {
        type: "logarithmic",
        grid: { display: true, drawBorder: true, borderColor: "black" },
        ticks: {
          callback: (value) => (value === 0 ? "" : formatTo2Decimals(value)),
        },
      },
      y: {
        grid: { display: false, drawBorder: false },
      },
    },
    plugins: {
      legend: { display: false },
      // datalabels: {
      //   color: "black",
      //   font: { weight: 600, size: 13, family: "Poppins" },
      //   formatter: (value) => (value === 0 ? "" : formatTo2Decimals(value)), // Hides datalabels for empty bars
      // }

      tooltip: {
        callbacks: {
          label: (ctx) => {
            // for horizontal bars, numeric value is in parsed.x
            const v = ctx.parsed?.x ?? ctx.raw;
            return `${ctx.dataset?.label ? ctx.dataset.label + ": " : ""}${formatTo2Decimals(v)}${unit ? ` ${unit}` : ""}`;
          },
        },
      },
      datalabels: {
        color: "black",
        font: { weight: 100, size: 12, family: "Poppins" },
        anchor: "end",
        align: "right",
        clamp: true,
        formatter: (raw) => {
          // raw can be a number or an object (e.g., {x, y})
          const v = raw == null ? null : typeof raw === "object" ? raw.x ?? raw.y ?? raw.value ?? null : raw;

          if (!v || Number(v) === 0) return ""; // keep your "hide zeros" behavior
          return formatTo2Decimals(v);
        },
      },
      title: {
        display: !!title,
        text: title,
        font: { family: "Poppins", size: 20, weight: 500 },
      },
    },
  };
  return (
    <Box sx={{ position: "relative" }} onMouseEnter={() => setVisibility(true)} onMouseLeave={() => setVisibility(false)}>
      <div style={{ position: "absolute", top: "10px", right: "10px" }}>
        <DownloadComponentButton ref={ref} filename={filename} visibility={visibility} />
      </div>
      <Box ref={ref} sx={{ backgroundColor: "white", paddingX: 4 }}>
        <div>
          <p>
            {totalLabel}
            <strong>
              {formatTo2Decimals(totalValue)} {unit}
            </strong>
          </p>
          <p>
            {averageLabel}
            <strong>
              {formatTo2Decimals(averageValue)} {unit}
            </strong>
          </p>
        </div>
        <Bar data={logSafeData} options={options} />
      </Box>
    </Box>
  );
};

export default TrackViewBarChart;
